import { z } from 'zod';
import {
  BOARD_TITLE_MAX_LENGTH,
  CARD_TITLE_MAX_LENGTH,
  SHORT_LINK_MAX_LENGTH,
  cardNumberSchema,
  cardReferenceSchema,
} from './board';
import { boardFilterQuerySchema } from './board-filter';
import { cardLocationSchema } from './card-location';
import { ulidSchema } from './id';
import { cardLabelSchema } from './label';

/**
 * Map view contracts — B54.
 *
 * The read model is route-specific and does not widen card/list/board schemas.
 * Pins group cards by the exact normalised coordinates supplied in
 * `cardLocationSchema`; location text is display text and searchable card text,
 * but it does not split or merge pins.
 */

export const boardMapQuerySchema = boardFilterQuerySchema;

export type BoardMapQuery = z.infer<typeof boardMapQuerySchema>;

export const mapBoardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  closed: z.boolean(),
});

export type MapBoardRef = z.infer<typeof mapBoardRefSchema>;

export const mapPinAppearanceSchema = z.discriminatedUnion('source', [
  z.object({
    source: z.literal('cover_colour'),
    colour: z.string().max(32),
  }),
  z.object({
    source: z.literal('cover_photo'),
    photoRef: z.string().max(2048),
    photoAttribution: z.string().max(512).nullable(),
  }),
  z.object({
    source: z.literal('cover_attachment'),
    attachmentId: ulidSchema,
  }),
  z.object({
    source: z.literal('label'),
    label: cardLabelSchema,
  }),
]);

export type MapPinAppearance = z.infer<typeof mapPinAppearanceSchema>;

export const mapCardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  cardNumber: cardNumberSchema,
  /** `KEY-NNNN` for a viewer who may use the board's references, else `null` — `cardReferenceSchema` in `./board`. */
  ref: cardReferenceSchema.nullable(),
  pinAppearance: mapPinAppearanceSchema.nullable(),
});

export type MapCardRef = z.infer<typeof mapCardRefSchema>;

export const mapPinSchema = z.object({
  location: cardLocationSchema,
  cards: z.array(mapCardRefSchema).min(1),
});

export type MapPin = z.infer<typeof mapPinSchema>;

export const boardMapSchema = z.object({
  board: mapBoardRefSchema,
  pins: z.array(mapPinSchema),
  excludedCardCount: z.number().int().nonnegative(),
});

export type BoardMap = z.infer<typeof boardMapSchema>;
